import { useCallback, useEffect, useRef, useState } from "react";

export const useSticky = ({
  triggerOn,
  offset = 0,
}: {
  triggerOn: "top" | "bottom" | "inView";
  offset?: number;
}) => {
  const ref = useRef(null);
  const [hasReachedRef, setHasReachedRef] = useState(false);

  const fixateBar = useCallback(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();

    if (triggerOn === "inView") {
      // Element is in view when its top enters viewport and bottom hasn't left viewport
      setHasReachedRef(
        rect.top <= window.innerHeight + offset && rect.bottom >= 0 + offset,
      );
    } else if (triggerOn === "top") {
      // If triggerOn is "top", we check if the top of the element is entering from the bottom of the viewport
      const trigger = rect.top; // Distance from the top of the viewport to the top of the element
      setHasReachedRef(
        trigger <= window.innerHeight + offset && trigger >= 0 + offset,
      );
    } else {
      // If triggerOn is "bottom", we check if the bottom of the element hits the top of the viewport
      const trigger = rect.bottom; // Distance from the top of the viewport to the bottom of the element
      setHasReachedRef(trigger <= 0 + offset);
    }
  }, [triggerOn, offset]);

  useEffect(() => {
    window.addEventListener("scroll", fixateBar);
    // Run once on mount to check initial position
    fixateBar();
    return () => {
      window.removeEventListener("scroll", fixateBar);
    };
  }, [fixateBar]);

  return [hasReachedRef, ref];
};
